import { AxiosError } from 'axios'
import produce from 'immer'
import { QueryObserverOptions, QueryStatus, UseQueryResult } from 'react-query'
import create, { State } from 'zustand'

import { flattenKubernetesTargets } from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { getContainersFromClients } from 'components/attacks/create/targets/target-utils'
import { AlfiClientResponse, ApplicationClient } from 'types/api/alfi/clients'
import {
  Client,
  ClientState,
  Container,
  InfraClientResponse
} from 'types/api/attacks/client'
import { K8sClient, K8sObject } from 'types/api/kubernetes/kubernetes'
import { naturalSortBy } from 'utils/list'

import { useGremlinQuery } from './use-gremlin-query'

export interface InfraClientsStoreState extends State {
  status: QueryStatus
  clients: {
    [id: string]: Client
  }
  set: (fn: (state: InfraClientsStoreState) => void) => void
  clearAll: () => void
}

export const useInfraClientsStore = create<InfraClientsStoreState>((set) => ({
  status: 'loading',
  clients: {},
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.clients = {}
      })
    )
}))

export const infraClientsSelector = (state: InfraClientsStoreState): Client[] =>
  naturalSortBy<Client>(
    Object.values<Client>(state.clients),
    [
      (target) => target.state === ClientState.Revoked,
      (target) => target.state === ClientState.Idle,
      (target) => target.state === ClientState.Active,
      (target) => target.identifier
    ],
    'asc'
  )

export const activeInfraClientSelector = (
  state: InfraClientsStoreState
): Client[] | ApplicationClient[] =>
  infraClientsSelector(state).filter((c) => c.state === ClientState.Active)

export const containerSelector = (
  state: InfraClientsStoreState
): Container[] => {
  const activeClients = activeInfraClientSelector(state)
  const containers = getContainersFromClients(activeClients)
  return containers
}

export function useInfraClients(
  queryOptions?: Partial<QueryObserverOptions<InfraClientResponse, AxiosError>>
): UseQueryResult<InfraClientResponse> {
  const set = useInfraClientsStore((state) => state.set)

  const clientsFetchState = useGremlinQuery<InfraClientResponse>(
    '/clients',
    {},
    {
      placeholderData: { active: [], idle: [], inactive: [] },
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      ...queryOptions,
      onSuccess: (data) => {
        Object.values(data).flat()
        set((state) => {
          state.clients = Object.fromEntries(
            Object.values(data)
              .flat()
              .map((c) => [c.identifier, c])
          )
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      }
    }
  )

  return clientsFetchState
}

export interface K8sClientsStoreState extends State {
  status: QueryStatus
  clients: {
    [id: string]: K8sClient
  }
  set: (fn: (state: K8sClientsStoreState) => void) => void
  clearAll: () => void
}

export const useK8sClientsStore = create<K8sClientsStoreState>((set) => ({
  status: 'loading',
  clients: {},
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.clients = {}
      })
    )
}))

export const k8sClientsSelector = (state: K8sClientsStoreState): K8sClient[] =>
  naturalSortBy<K8sClient>(
    Object.values<K8sClient>(state.clients),
    [
      (target) => target.state === ClientState.Revoked,
      (target) => target.state === ClientState.Idle,
      (target) => target.state === ClientState.Active,
      (target) => target.state === ClientState.Invited,
      (target) => target.clusterId
    ],
    'asc'
  )

export const activeK8sClientSelector = (
  state: K8sClientsStoreState
): K8sClient[] =>
  k8sClientsSelector(state).filter((c) => c.state === ClientState.Active)

export const objectsSelector = (state: K8sClientsStoreState): K8sObject[] =>
  flattenKubernetesTargets(activeK8sClientSelector(state))

export function useK8sClients(
  queryOptions?: Partial<QueryObserverOptions<K8sClient[], AxiosError>>
): UseQueryResult<K8sClient[]> {
  const set = useK8sClientsStore((state) => state.set)

  const k8sFetchState = useGremlinQuery<K8sClient[]>(
    '/kubernetes/targets',
    {},
    {
      placeholderData: [],
      refetchInterval: 30_000,
      ...queryOptions,
      onSuccess: (data) => {
        Object.values(data).flat()
        set((state) => {
          state.clients = Object.fromEntries(
            Object.values(data)
              .flat()
              .map((c) => [c.identifier, c])
          )
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      }
    }
  )

  return k8sFetchState
}

export interface AlfiClientsStoreState extends State {
  status: QueryStatus
  clients: {
    [id: string]: ApplicationClient
  }
  set: (fn: (state: AlfiClientsStoreState) => void) => void
  clearAll: () => void
}

export const useAlfiClientsStore = create<AlfiClientsStoreState>((set) => ({
  status: 'loading',
  clients: {},
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.clients = {}
      })
    )
}))

export const alfiClientsSelector = (
  state: AlfiClientsStoreState
): ApplicationClient[] =>
  naturalSortBy<ApplicationClient>(
    Object.values<ApplicationClient>(state.clients),
    [
      (target) => target.state === ClientState.Inactive,
      (target) => target.state === ClientState.Idle,
      (target) => target.state === ClientState.Active,
      (target) => target.name
    ],
    'asc'
  )

export const activeAlfiClientSelector = (
  state: AlfiClientsStoreState
): ApplicationClient[] =>
  alfiClientsSelector(state).filter((c) => c.state === ClientState.Active)

export function useAlfiClients(
  queryOptions?: Partial<QueryObserverOptions<AlfiClientResponse, AxiosError>>
): UseQueryResult<AlfiClientResponse> {
  const set = useAlfiClientsStore((state) => state.set)
  const alfiClientsFetchState = useGremlinQuery<AlfiClientResponse>(
    '/alfi/clients',
    {},
    {
      placeholderData: { active: [], idle: [], inactive: [] },
      refetchInterval: 30_000,
      ...queryOptions,
      onSuccess: (data) => {
        Object.values(data).flat()
        set((state) => {
          state.clients = Object.fromEntries(
            Object.values(data)
              .flat()
              .map((c) => [c.identifier, c])
          )
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      }
    }
  )

  return alfiClientsFetchState
}
